import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Copy, Trash2, TrendingUp, TrendingDown, DollarSign, CheckCircle2, ArrowRight, ChevronDown, Coins, Globe } from 'lucide-react';
import CryptoAffiliateBlock from '../../../components/CryptoAffiliateBlock';

const XrpProfitCalculatorTool = () => {
  const [buyPrice, setBuyPrice] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [investment, setInvestment] = useState('');
  const [fees, setFees] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');
  const [showCoinMenu, setShowCoinMenu] = useState(false);

  useEffect(() => {
    calculate();
  }, [buyPrice, sellPrice, investment, fees]);

  const calculate = () => {
    const buy = parseFloat(buyPrice);
    const sell = parseFloat(sellPrice);
    const invest = parseFloat(investment);
    const fee = parseFloat(fees) || 0;

    if (isNaN(buy) || isNaN(sell) || isNaN(invest) || buy <= 0 || invest <= 0) {
      setResult(null);
      return;
    }

    const xrpPurchased = invest / buy;
    const revenue = xrpPurchased * sell;
    const grossProfit = revenue - invest;
    const netProfit = grossProfit - fee;
    const roi = (netProfit / invest) * 100;
    const breakEven = (invest + fee) / xrpPurchased;

    setResult({
      xrpPurchased,
      revenue,
      grossProfit,
      netProfit,
      roi,
      breakEven,
      isProfitable: netProfit > 0,
      isBreakEven: Math.abs(netProfit) < 0.01
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(typeof value === 'number' ? value.toFixed(2) : value);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setBuyPrice('');
    setSellPrice('');
    setInvestment('');
    setFees('');
    setResult(null);
  };

  const formatCurrency = (value) => '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatPercent = (value) => (value >= 0 ? '+' : '') + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

  const scenarios = [
    { label: 'XRP: $0.50 → $1.00', buy: '0.50', sell: '1.00', invest: '1000' },
    { label: 'XRP: $0.30 → $0.80', buy: '0.30', sell: '0.80', invest: '3000' },
    { label: 'XRP: $1.00 → $3.00', buy: '1.00', sell: '3.00', invest: '5000' },
  ];

  const applyScenario = (scenario) => {
    setBuyPrice(scenario.buy);
    setSellPrice(scenario.sell);
    setInvestment(scenario.invest);
    setFees('5');
  };

  const coinOptions = [
    { name: 'XRP (Ripple)', slug: 'xrp-profit-calculator', icon: '✕', color: 'slate', current: true },
    { name: 'Bitcoin (BTC)', slug: 'bitcoin-profit-calculator', icon: '₿', color: 'orange', current: false },
    { name: 'Ethereum (ETH)', slug: 'ethereum-profit-calculator', icon: 'Ξ', color: 'indigo', current: false },
    { name: 'Solana (SOL)', slug: 'solana-profit-calculator', icon: '◎', color: 'violet', current: false },
    { name: 'Litecoin (LTC)', slug: 'litecoin-profit-calculator', icon: 'Ł', color: 'gray', current: false },
    { name: 'All Crypto', slug: 'crypto-profit-calculator', icon: '🪙', color: 'green', current: false },
  ];

  return (
    <div className="space-y-8" data-testid="xrp-profit-calculator">
      {/* Coin Selector */}
      <div className="relative">
        <button
          onClick={() => setShowCoinMenu(!showCoinMenu)}
          className="flex items-center gap-2 px-4 py-2 bg-slate-500/20 border border-slate-500/30 rounded-lg text-slate-300 hover:bg-slate-500/30 transition-colors"
          data-testid="coin-selector"
        >
          <span className="text-xl font-bold">✕</span>
          <span className="font-medium">XRP Profit Calculator</span>
          <ChevronDown size={16} className={`transition-transform ${showCoinMenu ? 'rotate-180' : ''}`} />
        </button>
        
        {showCoinMenu && (
          <div className="absolute top-full left-0 mt-2 w-72 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
            {coinOptions.map((coin) => (
              coin.current ? (
                <div key={coin.slug} className="flex items-center gap-3 px-4 py-3 bg-slate-500/10 text-slate-300 cursor-default">
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <CheckCircle2 size={16} className="ml-auto" />
                </div>
              ) : (
                <Link
                  key={coin.slug}
                  to={`/tools/${coin.slug}`}
                  className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700 transition-colors"
                  onClick={() => setShowCoinMenu(false)}
                >
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <ArrowRight size={16} className="ml-auto opacity-50" />
                </Link>
              )
            ))}
          </div>
        )}
      </div>

      {/* Quick Examples */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-400 mr-2">Quick Examples:</span>
        {scenarios.map((scenario, index) => (
          <button
            key={index}
            onClick={() => applyScenario(scenario)}
            className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-full text-gray-300 transition-colors"
          >
            {scenario.label}
          </button>
        ))}
      </div>

      {/* Calculator */}
      <div className="p-6 bg-gray-800 rounded-xl space-y-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-slate-500/20 flex items-center justify-center">
            <span className="text-2xl font-bold text-slate-300">✕</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">XRP Profit Calculator</h3>
            <p className="text-sm text-gray-400">Calculate your Ripple/XRP investment profit</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Buy Price (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={buyPrice}
                onChange={(e) => setBuyPrice(e.target.value)}
                placeholder="e.g., 0.50"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-slate-500 focus:outline-none text-lg"
                data-testid="buy-price-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Sell Price (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={sellPrice}
                onChange={(e) => setSellPrice(e.target.value)}
                placeholder="e.g., 1.00"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-slate-500 focus:outline-none text-lg"
                data-testid="sell-price-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Investment Amount (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={investment}
                onChange={(e) => setInvestment(e.target.value)}
                placeholder="e.g., 1000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-slate-500 focus:outline-none text-lg"
                data-testid="investment-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Trading Fees (USD) <span className="text-gray-500">(optional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={fees}
                onChange={(e) => setFees(e.target.value)}
                placeholder="e.g., 5"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-slate-500 focus:outline-none"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">XRP network fees are extremely low (~0.00001 XRP)</p>
          </div>
        </div>

        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors text-sm"
        >
          <Trash2 size={14} />
          Clear All
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          <div className={`p-6 rounded-xl border-2 ${result.isProfitable ? 'bg-green-500/10 border-green-500/40' : result.isBreakEven ? 'bg-yellow-500/10 border-yellow-500/40' : 'bg-red-500/10 border-red-500/40'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {result.isProfitable ? <TrendingUp className="text-green-400" size={28} /> : <TrendingDown className="text-red-400" size={28} />}
                <span className="text-lg font-medium text-gray-200">
                  {result.isProfitable ? 'Profit' : result.isBreakEven ? 'Break Even' : 'Loss'}
                </span>
              </div>
              <button onClick={() => copyResult(result.netProfit, 'profit')} className="p-2 hover:bg-gray-700 rounded-lg">
                {copied === 'profit' ? <CheckCircle2 size={18} className="text-green-400" /> : <Copy size={18} className="text-gray-400" />}
              </button>
            </div>
            <div className="text-center">
              <p className={`text-5xl font-bold ${result.isProfitable ? 'text-green-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {result.netProfit >= 0 ? '+' : ''}{formatCurrency(result.netProfit)}
              </p>
              <p className={`text-2xl mt-2 ${result.isProfitable ? 'text-green-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {formatPercent(result.roi)} ROI
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">XRP Purchased</p>
              <p className="text-xl font-semibold text-slate-300">{result.xrpPurchased.toFixed(2)} XRP</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Sale Value</p>
              <p className="text-xl font-semibold text-white">{formatCurrency(result.revenue)}</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Gross Profit</p>
              <p className={`text-xl font-semibold ${result.grossProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {result.grossProfit >= 0 ? '+' : ''}{formatCurrency(result.grossProfit)}
              </p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Break-even Price</p>
              <p className="text-xl font-semibold text-orange-400">{formatCurrency(result.breakEven)}</p>
            </div>
          </div>

          <CryptoAffiliateBlock 
            profit={result.netProfit} 
            cryptoName="XRP"
            toolName="XRP Profit Calculator"
            toolSlug="xrp-profit-calculator"
          />
        </div>
      )}

      {/* Related Calculators */}
      <div className="p-5 bg-gradient-to-r from-slate-500/10 to-gray-500/10 rounded-xl border border-slate-500/20">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Coins size={20} className="text-slate-400" />
          Related Crypto Calculators
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Link to="/tools/bitcoin-profit-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-orange-400">Bitcoin Profit</p>
              <p className="text-sm text-gray-400">BTC calculator</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-orange-400" />
          </Link>
          <Link to="/tools/ethereum-profit-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-indigo-400">Ethereum Profit</p>
              <p className="text-sm text-gray-400">ETH calculator</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-indigo-400" />
          </Link>
          <Link to="/tools/solana-profit-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-violet-400">Solana Profit</p>
              <p className="text-sm text-gray-400">SOL calculator</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-violet-400" />
          </Link>
          <Link to="/tools/crypto-tax-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-purple-400">Crypto Tax</p>
              <p className="text-sm text-gray-400">Tax calculator</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-purple-400" />
          </Link>
        </div>
      </div>

      {/* SEO Content */}
      <div className="space-y-8 pt-6 border-t border-gray-700">
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">What is an XRP Profit Calculator?</h2>
          <div className="prose prose-invert max-w-none text-gray-300 space-y-4">
            <p>
              An <strong>XRP Profit Calculator</strong> helps investors calculate profits from trading Ripple's native 
              cryptocurrency, XRP. As one of the oldest and most established cryptocurrencies, XRP is designed for 
              fast, low-cost international money transfers and is used by financial institutions worldwide.
            </p>
            <p>
              Our calculator determines your profit or loss by comparing your buy and sell prices, accounting for 
              trading fees, and showing your return on investment (ROI). XRP's extremely low transaction fees 
              (fractions of a cent) make it efficient for trading and transfers.
            </p>
            <p>
              XRP has a unique position in crypto — it's both a speculative asset and a utility token for Ripple's 
              payment network used by banks and remittance services. This calculator helps you track your investment 
              performance regardless of how you use XRP.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">How to Use the XRP Profit Calculator</h2>
          <div className="space-y-3">
            {[
              { step: 1, title: 'Enter Your XRP Buy Price', desc: 'Input the price per XRP when you purchased. XRP often trades below $1, so use decimals.' },
              { step: 2, title: 'Enter Your XRP Sell Price', desc: 'Input the price at which you sold (or plan to sell) your XRP holdings.' },
              { step: 3, title: 'Enter Investment Amount', desc: 'How much USD you invested. The calculator shows how many XRP tokens you acquired.' },
              { step: 4, title: 'Add Trading Fees (Optional)', desc: 'Include exchange fees. XRP network fees are negligible (~0.00001 XRP per transaction).' },
              { step: 5, title: 'View Your Results', desc: 'See profit/loss, ROI percentage, XRP quantity, and break-even price instantly.' },
            ].map((item) => (
              <div key={item.step} className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <div className="w-8 h-8 rounded-full bg-slate-500/20 text-slate-400 flex items-center justify-center font-bold flex-shrink-0">{item.step}</div>
                <div>
                  <h3 className="font-semibold text-white">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Example XRP Profit Calculation</h2>
          <div className="p-5 bg-gray-800 rounded-lg border border-gray-700">
            <h3 className="font-semibold text-white mb-3">Scenario: XRP Trade</h3>
            <div className="space-y-3 text-gray-300">
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Buy Price:</strong> $0.50 per XRP</li>
                <li><strong>Sell Price:</strong> $1.20 per XRP</li>
                <li><strong>Investment:</strong> $2,500</li>
                <li><strong>Trading Fees:</strong> $12.50</li>
              </ul>
              <div className="mt-4 p-4 bg-gray-900 rounded-lg font-mono text-sm">
                <p><span className="text-gray-400">XRP Purchased:</span> <span className="text-slate-300">$2,500 / $0.50 = 5,000 XRP</span></p>
                <p><span className="text-gray-400">Sale Value:</span> <span className="text-white">5,000 XRP × $1.20 = $6,000</span></p>
                <p><span className="text-gray-400">Gross Profit:</span> <span className="text-green-400">$6,000 - $2,500 = $3,500</span></p>
                <p><span className="text-gray-400">Net Profit:</span> <span className="text-green-400">$3,500 - $12.50 = $3,487.50</span></p>
                <p><span className="text-gray-400">ROI:</span> <span className="text-blue-400">($3,487.50 / $2,500) × 100 = 139.5%</span></p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: 'What makes XRP different from Bitcoin?', a: 'XRP is designed for fast, cheap cross-border payments (3-5 seconds, ~$0.0002 fee). Bitcoin prioritizes decentralization and store of value. XRP is used by banks and financial institutions via RippleNet.' },
              { q: 'How did the SEC lawsuit affect XRP?', a: 'The SEC sued Ripple in 2020, causing major price drops and delistings. In 2023, a partial ruling clarified XRP isn\'t always a security. This legal clarity has helped XRP recover and get relisted on major exchanges.' },
              { q: 'What is XRP\'s all-time high?', a: 'XRP reached approximately $3.40 in January 2018. Many investors calculate potential profits based on XRP returning to or exceeding this level.' },
              { q: 'Can I stake XRP?', a: 'XRP uses a consensus mechanism (not proof-of-stake), so traditional staking isn\'t available. Some platforms offer lending/yield programs for XRP, but these carry counterparty risk.' },
              { q: 'Why are XRP transaction fees so low?', a: 'XRP uses a unique consensus protocol that doesn\'t require mining. Transactions settle in 3-5 seconds with fees of ~0.00001 XRP (~$0.00001), making it one of the most efficient cryptocurrencies for transfers.' },
            ].map((faq, i) => (
              <div key={i} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <h3 className="font-semibold text-white mb-2">{faq.q}</h3>
                <p className="text-gray-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="p-4 bg-gradient-to-r from-slate-500/10 to-blue-500/10 rounded-lg border border-slate-500/20">
        <h4 className="text-sm font-medium text-gray-200 mb-2 flex items-center gap-2">
          <Globe size={16} className="text-slate-400" />
          XRP for Cross-Border Payments
        </h4>
        <p className="text-sm text-gray-400">
          XRP enables near-instant international transfers at a fraction of traditional banking costs. Ripple's 
          On-Demand Liquidity (ODL) service uses XRP as a bridge currency, creating real-world utility and demand 
          beyond speculation.
        </p>
      </div>
    </div>
  );
};

export default XrpProfitCalculatorTool;
